import type { PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";

export type Dimensions3D = {
  width: number;
  height: number;
  depth: number;
};

export type DimensionAxis = keyof Dimensions3D;

/**
 * The front/side/top axis table: the six ways a product can sit, each a
 * permutation of its three measurements onto the planogram's axes. One copy,
 * because a box that changes shape between modules is a bug; it was written
 * out three times before. `SIDE` is turned onto its side, `TOP` laid on its
 * back, and the `2` suffix is the same face given a quarter turn.
 *
 * PSA codes 0-5 map onto these in order; above 5 they repeat `code % 6`.
 */
export const getOrientedDimensions = (
  { width, height, depth }: Dimensions3D,
  orientation: PlanogramItemOrientation,
): Dimensions3D => {
  switch (orientation) {
    case "FRONT":
      return { width, height, depth };
    case "FRONT2":
      return { width: height, height: width, depth };
    case "SIDE":
      return { width: depth, height, depth: width };
    case "SIDE2":
      return { width: height, height: depth, depth: width };
    case "TOP":
      return { width, height: depth, depth: height };
    case "TOP2":
      return { width: depth, height: width, depth: height };
    default:
      return { width, height, depth };
  }
};

/** One axis of {@link getOrientedDimensions}, for call sites that want a single number. */
export const getOrientedDimension = (
  dimensions: Dimensions3D,
  dimension: DimensionAxis,
  orientation: PlanogramItemOrientation,
): number => getOrientedDimensions(dimensions, orientation)[dimension];

/**
 * The inverse of {@link getOrientedDimensions}. A PSA Position states the
 * block as drawn, already rotated, while the Product record states the box as
 * it measures: deriving one from the other without un-rotating applies the
 * orientation twice.
 */
export const getDeorientedDimensions = (
  { width, height, depth }: Dimensions3D,
  orientation: PlanogramItemOrientation,
): Dimensions3D => {
  switch (orientation) {
    case "FRONT":
      return { width, height, depth };
    case "FRONT2":
      return { width: height, height: width, depth };
    case "SIDE":
      return { width: depth, height, depth: width };
    case "SIDE2":
      return { width: depth, height: width, depth: height };
    case "TOP":
      return { width, height: depth, depth: height };
    case "TOP2":
      return { width: height, height: depth, depth: width };
    default:
      return { width, height, depth };
  }
};
